import fs from 'fs-extra';
import path from 'path';
import { Logger } from './logger';

/**
 * 파일 유틸리티 클래스
 */
export class FileUtils {
  /**
   * 디렉토리가 존재하는지 확인
   */
  public static async directoryExists(dirPath: string): Promise<boolean> {
    try {
      const stats = await fs.stat(dirPath);
      return stats.isDirectory();
    } catch {
      return false;
    }
  }

  /**
   * 파일이 존재하는지 확인
   */
  public static async fileExists(filePath: string): Promise<boolean> {
    try {
      const stats = await fs.stat(filePath);
      return stats.isFile();
    } catch {
      return false;
    }
  }

  /**
   * 디렉토리 생성
   */
  public static async createDirectory(dirPath: string): Promise<void> {
    try {
      await fs.ensureDir(dirPath);
      Logger.debug(`디렉토리 생성: ${dirPath}`);
    } catch (error) {
      throw new Error(`디렉토리 생성 실패: ${dirPath} - ${error}`);
    }
  }

  /**
   * 파일 쓰기
   */
  public static async writeFile(filePath: string, content: string): Promise<void> {
    try {
      await fs.ensureDir(path.dirname(filePath));
      await fs.writeFile(filePath, content, 'utf8');
      Logger.debug(`파일 생성: ${filePath}`);
    } catch (error) {
      throw new Error(`파일 쓰기 실패: ${filePath} - ${error}`);
    }
  }

  /**
   * 파일 읽기
   */
  public static async readFile(filePath: string): Promise<string> {
    try {
      return await fs.readFile(filePath, 'utf8');
    } catch (error) {
      throw new Error(`파일 읽기 실패: ${filePath} - ${error}`);
    }
  }

  /**
   * JSON 파일 읽기
   */
  public static async readJsonFile<T>(filePath: string): Promise<T> {
    try {
      return await fs.readJson(filePath);
    } catch (error) {
      throw new Error(`JSON 파일 읽기 실패: ${filePath} - ${error}`);
    }
  }

  /**
   * JSON 파일 쓰기
   */
  public static async writeJsonFile(filePath: string, data: unknown): Promise<void> {
    try {
      await fs.ensureDir(path.dirname(filePath));
      await fs.writeJson(filePath, data, { spaces: 2 });
      Logger.debug(`JSON 파일 생성: ${filePath}`);
    } catch (error) {
      throw new Error(`JSON 파일 쓰기 실패: ${filePath} - ${error}`);
    }
  }

  /**
   * 파일 복사
   */
  public static async copyFile(src: string, dest: string): Promise<void> {
    try {
      await fs.ensureDir(path.dirname(dest));
      await fs.copy(src, dest);
      Logger.debug(`파일 복사: ${src} -> ${dest}`);
    } catch (error) {
      throw new Error(`파일 복사 실패: ${src} -> ${dest} - ${error}`);
    }
  }

  /**
   * 디렉토리 복사
   */
  public static async copyDirectory(src: string, dest: string): Promise<void> {
    try {
      await fs.copy(src, dest);
      Logger.debug(`디렉토리 복사: ${src} -> ${dest}`);
    } catch (error) {
      throw new Error(`디렉토리 복사 실패: ${src} -> ${dest} - ${error}`);
    }
  }

  /**
   * 파일 또는 디렉토리 삭제
   */
  public static async remove(targetPath: string): Promise<void> {
    try {
      await fs.remove(targetPath);
      Logger.debug(`삭제: ${targetPath}`);
    } catch (error) {
      throw new Error(`삭제 실패: ${targetPath} - ${error}`);
    }
  }

  /**
   * 상대 경로를 절대 경로로 변환
   */
  public static resolvePath(...paths: string[]): string {
    return path.resolve(...paths);
  }

  /**
   * 현재 작업 디렉토리 반환
   */
  public static getCurrentDirectory(): string {
    return process.cwd();
  }
}
